import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { TableBody as AriaTableBody } from 'react-aria-components'
import { Table, TableCard } from '@/components/application/table/table'
import { Button } from '@/components/base/buttons'
import { Badge } from '@/components/ui/Badge'
import { Select } from '@/components/ui/Select'
import { Dropdown } from '@/components/base/dropdown/dropdown'
import { AlertDialog } from '@/components/ui/AlertDialog'
import { PlusIcon, Edit01Icon, Trash01Icon, Copy01Icon } from '@untitledui-icons/react/line'
import {
  useSimScenarios,
  useSimTags,
  useCreateScenario,
  useUpdateScenario,
  useDeleteScenario,
} from '@/hooks/api/useSimulations'
import { ScenarioFormDialog } from './ScenarioFormDialog'
import { toast } from '@/lib/toast'
import type { SimScenario, SimScenarioInput } from '@/types/simulation'
import type { SortDescriptor } from 'react-aria-components'

/**
 * Scenarios tab - CRUD for simulation scenarios
 */
export function SimScenariosTab() {
  const { t } = useTranslation()

  // State
  const [tagFilter, setTagFilter] = useState<string>('')
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingScenario, setEditingScenario] = useState<SimScenario | null>(null)
  const [isCloning, setIsCloning] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<SimScenario | null>(null)
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'id',
    direction: 'descending',
  })

  // Fetch available tags
  const { data: availableTags = [] } = useSimTags()

  // Fetch scenarios
  const { data: scenarios = [], isLoading, isFetching } = useSimScenarios({
    tagIds: tagFilter ? [Number(tagFilter)] : undefined,
  })

  // Mutations
  const createScenario = useCreateScenario()
  const updateScenario = useUpdateScenario()
  const deleteScenario = useDeleteScenario()

  // Sort scenarios client-side
  const sortedScenarios = [...scenarios].sort((a, b) => {
    const column = sortDescriptor.column as keyof SimScenario
    const aVal = a[column]
    const bVal = b[column]
    if (aVal === null || aVal === undefined) return 1
    if (bVal === null || bVal === undefined) return -1
    const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0
    return sortDescriptor.direction === 'descending' ? -cmp : cmp
  })

  // Handlers
  const handleSortChange = useCallback((descriptor: SortDescriptor) => {
    setSortDescriptor(descriptor)
  }, [])

  const handleTagFilter = useCallback((key: string | number | null) => {
    setTagFilter(key ? String(key) : '')
  }, [])

  const handleCreate = useCallback(() => {
    setEditingScenario(null)
    setIsFormOpen(true)
  }, [])

  const handleEdit = useCallback((scenario: SimScenario) => {
    setEditingScenario(scenario)
    setIsCloning(false)
    setIsFormOpen(true)
  }, [])

  const handleClone = useCallback((scenario: SimScenario) => {
    // Create a clone with modified name for pre-population
    const clonedScenario: SimScenario = {
      ...scenario,
      name: `${scenario.name} (${t('resources.generic.copy')})`,
    }
    setEditingScenario(clonedScenario)
    setIsCloning(true)
    setIsFormOpen(true)
  }, [t])

  const handleDelete = useCallback((scenario: SimScenario) => {
    setDeleteTarget(scenario)
  }, [])

  const handleFormSubmit = useCallback(
    async (data: SimScenarioInput) => {
      try {
        // When cloning, always create a new scenario
        if (editingScenario && !isCloning) {
          await updateScenario.mutateAsync({ id: editingScenario.id, data })
          toast.success(t('pages.simulations.scenarios.update_success'))
        } else {
          await createScenario.mutateAsync(data)
          toast.success(
            isCloning
              ? t('pages.simulations.scenarios.clone_success')
              : t('pages.simulations.scenarios.create_success')
          )
        }
        setIsFormOpen(false)
        setIsCloning(false)
      } catch {
        toast.error(
          editingScenario && !isCloning
            ? t('pages.simulations.scenarios.update_error')
            : t('pages.simulations.scenarios.create_error')
        )
      }
    },
    [editingScenario, isCloning, createScenario, updateScenario, t]
  )

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return
    try {
      await deleteScenario.mutateAsync(deleteTarget.id)
      toast.success(t('pages.simulations.scenarios.delete_success'))
      setDeleteTarget(null)
    } catch {
      toast.error(t('pages.simulations.scenarios.delete_error'))
    }
  }, [deleteTarget, deleteScenario, t])

  // Tag options for filter
  const tagOptions = [
    { value: '', label: t('resources.generic.all') },
    ...availableTags.map((tag) => ({
      value: String(tag.id),
      label: tag.name,
    })),
  ]

  // Header content with filter and create button
  const headerContent = (
    <div className="flex items-center gap-3">
      {/* Fetching indicator */}
      {isFetching && !isLoading && (
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
      )}
      <div className="w-36">
        <Select
          options={tagOptions}
          selectedKey={tagFilter}
          onSelectionChange={handleTagFilter}
          placeholder={t('pages.simulations.filters.tag')}
          aria-label={t('pages.simulations.filters.tag')}
          size="sm"
        />
      </div>
      <Button onClick={handleCreate} icon={PlusIcon} size="sm">
        {t('pages.simulations.scenarios.create')}
      </Button>
    </div>
  )

  // Loading state
  if (isLoading) {
    return (
      <TableCard.Root size="sm" className="flex h-full flex-col">
        <TableCard.Header
          title={t('pages.simulations.scenarios.title')}
          contentTrailing={headerContent}
        />
        <div className="flex flex-1 items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
        </div>

        {/* Form Dialog */}
        <ScenarioFormDialog
          isOpen={isFormOpen}
          onClose={() => {
            setIsFormOpen(false)
            setIsCloning(false)
          }}
          onSubmit={handleFormSubmit}
          scenario={editingScenario}
          isClone={isCloning}
          isLoading={createScenario.isPending || updateScenario.isPending}
        />

        {/* Delete Confirmation */}
        <AlertDialog
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          title={t('pages.simulations.scenarios.delete_title')}
          description={t('pages.simulations.scenarios.delete_description', {
            name: deleteTarget?.name,
          })}
          confirmLabel={t('resources.generic.delete')}
          onConfirm={handleConfirmDelete}
          isDestructive
          isLoading={deleteScenario.isPending}
        />
      </TableCard.Root>
    )
  }

  // Empty state
  if (sortedScenarios.length === 0) {
    return (
      <TableCard.Root size="sm" className="flex h-full flex-col">
        <TableCard.Header
          title={t('pages.simulations.scenarios.title')}
          contentTrailing={headerContent}
        />
        <div className="flex flex-1 flex-col items-center justify-center gap-4 py-12">
          <p className="text-gray-500">{t('pages.simulations.scenarios.empty')}</p>
          <Button onClick={handleCreate} icon={PlusIcon}>
            {t('pages.simulations.scenarios.create')}
          </Button>
        </div>

        {/* Form Dialog */}
        <ScenarioFormDialog
          isOpen={isFormOpen}
          onClose={() => {
            setIsFormOpen(false)
            setIsCloning(false)
          }}
          onSubmit={handleFormSubmit}
          scenario={editingScenario}
          isClone={isCloning}
          isLoading={createScenario.isPending || updateScenario.isPending}
        />

        {/* Delete Confirmation */}
        <AlertDialog
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          title={t('pages.simulations.scenarios.delete_title')}
          description={t('pages.simulations.scenarios.delete_description', {
            name: deleteTarget?.name,
          })}
          confirmLabel={t('resources.generic.delete')}
          onConfirm={handleConfirmDelete}
          isDestructive
          isLoading={deleteScenario.isPending}
        />
      </TableCard.Root>
    )
  }

  return (
    <TableCard.Root size="sm" className="flex h-full flex-col">
      <TableCard.Header
        title={t('pages.simulations.scenarios.title')}
        contentTrailing={headerContent}
      />

      <div className="flex-1 overflow-auto">
        <Table
          aria-label={t('pages.simulations.scenarios.title')}
          sortDescriptor={sortDescriptor}
          onSortChange={handleSortChange}
        >
          <Table.Header>
            <Table.Head id="id" label={t('pages.simulations.scenarios.columns.id')} allowsSorting isRowHeader />
            <Table.Head id="name" label={t('pages.simulations.scenarios.columns.name')} allowsSorting />
            <Table.Head id="tags" label={t('pages.simulations.scenarios.columns.tags')} />
            <Table.Head id="priority" label={t('pages.simulations.scenarios.columns.priority')} allowsSorting />
            <Table.Head id="max_turns" label={t('pages.simulations.scenarios.columns.max_turns')} />
            <Table.Head id="actions" label="" />
          </Table.Header>

          <AriaTableBody>
            {sortedScenarios.map((scenario) => (
              <Table.Row key={scenario.id}>
                <Table.Cell className="w-16 font-medium">#{scenario.id}</Table.Cell>
                <Table.Cell className="max-w-xs">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-gray-900 dark:text-gray-100">
                      {scenario.name}
                    </div>
                    <div className="truncate text-sm text-gray-500 dark:text-gray-400">
                      {scenario.scenario_prompt}
                    </div>
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <div className="flex flex-wrap gap-1">
                    {scenario.tags && scenario.tags.length > 0 ? (
                      scenario.tags.map((tag) => (
                        <Badge key={tag.id} variant="gray" size="sm">
                          {tag.name}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-fg-tertiary text-sm">-</span>
                    )}
                  </div>
                </Table.Cell>
                <Table.Cell className="w-16 whitespace-nowrap">{scenario.priority}</Table.Cell>
                <Table.Cell className="w-20 whitespace-nowrap">{scenario.max_turns}</Table.Cell>
                <Table.Cell className="w-12">
                  <Dropdown.Root>
                    <Dropdown.DotsButton />
                    <Dropdown.Popover className="w-min">
                      <Dropdown.Menu>
                        <Dropdown.Item icon={Edit01Icon} onAction={() => handleEdit(scenario)}>
                          {t('resources.generic.edit')}
                        </Dropdown.Item>
                        <Dropdown.Item icon={Copy01Icon} onAction={() => handleClone(scenario)}>
                          {t('pages.simulations.scenarios.clone')}
                        </Dropdown.Item>
                        <Dropdown.Item icon={Trash01Icon} onAction={() => handleDelete(scenario)}>
                          {t('resources.generic.delete')}
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown.Popover>
                  </Dropdown.Root>
                </Table.Cell>
              </Table.Row>
            ))}
          </AriaTableBody>
        </Table>
      </div>

      {/* Form Dialog */}
      <ScenarioFormDialog
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false)
          setIsCloning(false)
        }}
        onSubmit={handleFormSubmit}
        scenario={editingScenario}
        isClone={isCloning}
        isLoading={createScenario.isPending || updateScenario.isPending}
      />

      {/* Delete Confirmation */}
      <AlertDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={t('pages.simulations.scenarios.delete_title')}
        description={t('pages.simulations.scenarios.delete_description', {
          name: deleteTarget?.name,
        })}
        confirmLabel={t('resources.generic.delete')}
        onConfirm={handleConfirmDelete}
        isDestructive
        isLoading={deleteScenario.isPending}
      />
    </TableCard.Root>
  )
}

export default SimScenariosTab
